/**
 * Report aggregato delle newsletter (voce di menu "Report newsletter").
 *
 * Mostra, per le entità su cui l'utente ha l'autorizzazione NEWSLETTER:
 *  - KPI di periodo (newsletter partite, email inviate, tasso di apertura/click, bounce, spam);
 *  - volumi mensili (barre) e tassi mensili (linee) — due grafici distinti, MAI un doppio asse:
 *    volumi e percentuali hanno scale incomparabili;
 *  - ripartizione per entità mittente (solo se l'utente ne governa più di una);
 *  - campagne con la resa migliore, da cui si salta al report della singola newsletter.
 *
 * Dati: GET /newsletters/report (aggregazioni fatte in SQL lato BE).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
//--------------------------------------------------------
import { HttpErrorHandler, ErrorUI } from '../../../utils/ErrorHandler';
import { LoadingUI, EmptyPageUI } from '../../../utils/LayoutStyles';
import { DefaultOutlinedButton } from '../../../utils/Buttons';
import { CustomAutocomplete } from '../../../utils/StyledInputs';
import { newslettersApi } from '../../../services/newsletter/newslettersApi';
import Chart, { useChart } from '../../../components/chart';

/**
 * Colori delle serie: slot categorici 1 e 2 della palette di riferimento.
 * Coppia validata (CVD ΔE 24.7, normal-vision ΔE 33.6, contrasto ≥ 3:1 sul fondo chiaro).
 */
const SERIES_OPENED = '#2a78d6';
const SERIES_CLICKED = '#eb6834';
const VOLUME_COLOR = '#2a78d6';

/** Periodi selezionabili: mesi indietro da oggi. */
const PERIODS = [
  { value: 3, label: 'Ultimi 3 mesi' },
  { value: 6, label: 'Ultimi 6 mesi' },
  { value: 12, label: 'Ultimi 12 mesi' },
  { value: 36, label: 'Ultimi 3 anni' },
];

/** Attesa prima di interrogare il BE mentre si digita nella ricerca entità. */
const SEARCH_DEBOUNCE_MS = 400;

const fmtInt = (v) => Number(v ?? 0).toLocaleString('it-IT');
const fmtPct = (v) => `${Number(v ?? 0).toFixed(1).replace('.', ',')}%`;
/** 'YYYY-MM' → 'gen 2026' */
const fmtMonth = (m) => dayjs(`${m}-01`).format('MMM YYYY');

/** Riquadro KPI: numero grande + etichetta, senza grafico (nessun trend da mostrare qui). */
function StatTile({ label, value, hint }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="body2" sx={{ color: '#637381' }}>
          {label}
        </Typography>
        <Typography variant="h3" sx={{ mt: 0.5, color: '#212b36' }}>
          {value}
        </Typography>
        {hint && (
          <Typography variant="caption" sx={{ color: '#919eab' }}>
            {hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
StatTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.string,
};

export default function NewsletterReport() {
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentProfile, setCurrentProfile] = useState('');

  const [months, setMonths] = useState(12);

  /**
   * Filtro entità: l'elenco NON può essere un select precaricato — un utente autorizzato
   * a livello nazionale ha migliaia di entità discendenti. È un autocomplete che interroga
   * il BE ad ogni ricerca, che restituisce solo le prime per volume di invio.
   */
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityOptions, setEntityOptions] = useState([]);
  const [entitySearch, setEntitySearch] = useState('');
  const [loadingEntities, setLoadingEntities] = useState(false);
  const entityTimerRef = useRef(null);

  const entityId = selectedEntity?.entity_id ?? 'all';

  const loadReport = useCallback((monthsBack, entity) => {
    const params = { date_from: dayjs().subtract(monthsBack, 'month').format('YYYY-MM-DD') };
    if (entity && entity !== 'all') params.entity_id = entity;

    setLoading(true);
    newslettersApi
      .report(params)
      .then((res) => {
        setReport(res.data ?? null);
        setErrorMessage('');
      })
      .catch((error) => HttpErrorHandler(error, setErrorMessage))
      .finally(() => {
        setLoading(false);
        setReady(true);
      });
  }, []);

  // Profilo corrente (guard ADMIN, coerente con le altre pagine newsletter).
  useEffect(() => {
    let data;
    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));
    if (data) setCurrentProfile(data.roles.filter((role) => role.last_used));
  }, []);

  useEffect(() => {
    loadReport(months, entityId);
  }, [months, entityId, loadReport]);

  // Opzioni del filtro entità: ricerca server-side con debounce, rifatta anche al cambio periodo
  // (un'entità senza invii nel nuovo periodo non deve restare proponibile).
  useEffect(() => {
    clearTimeout(entityTimerRef.current);
    setLoadingEntities(true);

    entityTimerRef.current = setTimeout(() => {
      newslettersApi
        .reportEntities({
          date_from: dayjs().subtract(months, 'month').format('YYYY-MM-DD'),
          ...(entitySearch ? { search: entitySearch } : {}),
        })
        .then((res) => setEntityOptions(Array.isArray(res.data) ? res.data : []))
        .catch(() => setEntityOptions([]))
        .finally(() => setLoadingEntities(false));
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(entityTimerRef.current);
  }, [entitySearch, months]);

  const totals = report?.totals ?? {};
  const byMonth = report?.by_month ?? [];
  const byEntity = report?.by_entity ?? [];
  // Entità mittenti distinte nel periodo: decide se il filtro ha senso, a prescindere
  // da quante righe la tabella ne mostri.
  const entitiesCount = report?.entities_count ?? 0;
  const topNewsletters = report?.top_newsletters ?? [];
  const monthLabels = byMonth.map((m) => fmtMonth(m.month));

  // Volumi mensili: serie unica → nessuna legenda, il titolo della card nomina la serie.
  const volumeOptions = useChart({
    colors: [VOLUME_COLOR],
    chart: { type: 'bar' },
    plotOptions: { bar: { columnWidth: '45%', borderRadius: 4, borderRadiusApplication: 'end' } },
    legend: { show: false },
    dataLabels: { enabled: false },
    xaxis: { categories: monthLabels },
    yaxis: { labels: { formatter: (v) => fmtInt(v) } },
    tooltip: { y: { formatter: (v) => `${fmtInt(v)} email` } },
    grid: { borderColor: '#f0f0f0', strokeDashArray: 3 },
  });

  // Tassi mensili: due serie nella STESSA unità (%), quindi un solo asse.
  const rateOptions = useChart({
    colors: [SERIES_OPENED, SERIES_CLICKED],
    chart: { type: 'line' },
    stroke: { width: 2, curve: 'smooth' },
    markers: { size: 4, strokeWidth: 2, strokeColors: '#ffffff', hover: { size: 6 } },
    legend: { show: true, position: 'top', horizontalAlign: 'right' },
    dataLabels: { enabled: false },
    xaxis: { categories: monthLabels },
    yaxis: { min: 0, labels: { formatter: (v) => `${Math.round(v)}%` } },
    tooltip: { shared: true, intersect: false, y: { formatter: (v) => fmtPct(v) } },
    grid: { borderColor: '#f0f0f0', strokeDashArray: 3 },
  });

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  const hasData = (totals.newsletters ?? 0) > 0;

  return (
    <>
      <Helmet>
        <title>Report Newsletter</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* Header + filtri di periodo/entità su una riga sola, sopra i contenuti. */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <DefaultOutlinedButton onClick={() => navigate('/admin/newsletter')} startIcon={<ArrowBackIcon />}>
              Indietro
            </DefaultOutlinedButton>
            <Typography variant="h4">Report Newsletter</Typography>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              select
              size="small"
              label="Periodo"
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
              sx={{ minWidth: 170 }}
            >
              {PERIODS.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>

            {/* Il filtro resta visibile anche quando è già attivo: un filtro che scompare
                dopo l'uso non si può più rimuovere, e per tornare a vedere tutte le entità
                occorrerebbe ricaricare la pagina. */}
            {(entitiesCount > 1 || selectedEntity) && (
              <CustomAutocomplete
                id="report-entity"
                size="small"
                options={entityOptions}
                value={selectedEntity}
                loading={loadingEntities}
                // La ricerca è già fatta dal BE: nessun ulteriore filtro lato client.
                filterOptions={(x) => x}
                isOptionEqualToValue={(option, value) => option.entity_id === value.entity_id}
                getOptionLabel={(option) => option.entity_name ?? ''}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.entity_id}>
                    {option.entity_name}
                  </Box>
                )}
                onInputChange={(_e, value, reason) => {
                  if (reason === 'input') setEntitySearch(value);
                  if (reason === 'clear') setEntitySearch('');
                }}
                onChange={(_e, value) => setSelectedEntity(value)}
                noOptionsText={entitySearch ? 'Nessuna entità trovata' : 'Digita per cercare'}
                customStyles={{ minWidth: 260 }}
                renderInput={(params) => (
                  <TextField {...params} label="Tutte le entità" variant="outlined" size="small" />
                )}
              />
            )}
          </Stack>
        </Stack>

        {/* eslint-disable-next-line no-nested-ternary */}
        {!ready || loading ? (
          LoadingUI()
        ) : errorMessage.length > 0 ? (
          ErrorUI(errorMessage)
        ) : !hasData ? (
          <Card>
            <Box sx={{ p: 4 }}>{EmptyPageUI('Nessuna newsletter inviata nel periodo selezionato')}</Box>
          </Card>
        ) : (
          <Stack spacing={3}>
            {/* KPI di periodo */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <StatTile label="Newsletter inviate" value={fmtInt(totals.newsletters)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatTile
                  label="Email inviate"
                  value={fmtInt(totals.sent)}
                  hint={`su ${fmtInt(totals.included)} destinatari inclusi`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatTile
                  label="Tasso di apertura"
                  value={fmtPct(totals.open_rate)}
                  hint={`${fmtInt(totals.opened)} aperture`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatTile
                  label="Tasso di click"
                  value={fmtPct(totals.click_rate)}
                  hint={`${fmtInt(totals.clicked)} click`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatTile
                  label="Bounce"
                  value={fmtPct(totals.bounce_rate)}
                  hint={`${fmtInt(totals.soft_bounce)} soft · ${fmtInt(totals.hard_bounce)} hard`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatTile label="Segnalate come spam" value={fmtInt(totals.spam)} hint={fmtPct(totals.spam_rate)} />
              </Grid>
            </Grid>

            {/* Volumi e tassi: due grafici separati, un solo asse ciascuno. */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardHeader
                    title="Email inviate per mese"
                    titleTypographyProps={{ variant: 'h6' }}
                    subheader="Volume complessivo delle spedizioni"
                  />
                  <CardContent>
                    <Chart
                      type="bar"
                      series={[{ name: 'Email inviate', data: byMonth.map((m) => m.sent) }]}
                      options={volumeOptions}
                      height={300}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardHeader
                    title="Aperture e click per mese"
                    titleTypographyProps={{ variant: 'h6' }}
                    subheader="Percentuali calcolate sulle email inviate"
                  />
                  <CardContent>
                    <Chart
                      type="line"
                      series={[
                        { name: 'Aperture', data: byMonth.map((m) => m.open_rate) },
                        { name: 'Click', data: byMonth.map((m) => m.click_rate) },
                      ]}
                      options={rateOptions}
                      height={300}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Ripartizione per entità: ha senso solo se l'utente ne governa più di una. */}
            {byEntity.length > 1 && (
              <Card>
                <CardHeader
                  title="Per entità mittente"
                  titleTypographyProps={{ variant: 'h6' }}
                  subheader={
                    entitiesCount > byEntity.length - 1 && byEntity[byEntity.length - 1]?.entity_id === null
                      ? `Prime entità per volume su ${fmtInt(entitiesCount)} totali`
                      : undefined
                  }
                />
                <TableContainer sx={{ p: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Entità</TableCell>
                        <TableCell align="right">Newsletter</TableCell>
                        <TableCell align="right">Email inviate</TableCell>
                        <TableCell align="right">Aperture</TableCell>
                        <TableCell align="right">Click</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {byEntity.map((e) => (
                        <TableRow key={e.entity_id ?? 'others'} hover>
                          {/* La riga di residuo non è un'entità: niente grassetto, solo corsivo attenuato. */}
                          <TableCell sx={e.entity_id ? undefined : { fontStyle: 'italic', color: '#637381' }}>
                            {e.entity_name}
                          </TableCell>
                          <TableCell align="right">{fmtInt(e.newsletters)}</TableCell>
                          <TableCell align="right">{fmtInt(e.sent)}</TableCell>
                          <TableCell align="right">{fmtPct(e.open_rate)}</TableCell>
                          <TableCell align="right">{fmtPct(e.click_rate)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            )}

            {/* Campagne migliori: porta al report della singola newsletter già esistente. */}
            <Card>
              <CardHeader
                title="Campagne con il miglior tasso di apertura"
                titleTypographyProps={{ variant: 'h6' }}
                subheader="Clicca una riga per aprirne il report dettagliato"
              />
              <TableContainer sx={{ p: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Inviata il</TableCell>
                      <TableCell align="right">Email inviate</TableCell>
                      <TableCell align="right">Aperture</TableCell>
                      <TableCell align="right">Click</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topNewsletters.map((n) => (
                      <TableRow
                        key={n.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate('/admin/newsletter/tracking', { state: { newsID: n.id } })}
                      >
                        <TableCell>{n.name}</TableCell>
                        <TableCell>{n.sent_at ? dayjs(n.sent_at).format('DD/MM/YYYY HH:mm') : '—'}</TableCell>
                        <TableCell align="right">{fmtInt(n.sent)}</TableCell>
                        <TableCell align="right">{fmtPct(n.open_rate)}</TableCell>
                        <TableCell align="right">{fmtPct(n.click_rate)}</TableCell>
                        <TableCell align="right" sx={{ color: '#919EAB' }}>
                          <KeyboardArrowRightIcon fontSize="small" sx={{ display: 'block' }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Stack>
        )}
      </Container>
    </>
  );
}
